<template>
  <div>
    <el-row :gutter="5">
      <el-col :span="showTextFilter ? 10 : 20" v-if="showCategoryFilter">
        <el-select
          v-model="computedCategory"
          clearable
          filterable
          placeholder="Category"
          size="mini"
        >
          <el-option
            v-for="category in categoryList"
            :key="category.id"
            :label="category.title"
            :value="category.id"
          ></el-option>
        </el-select>
      </el-col>
      <el-col :span="showCategoryFilter ? 10 : 20" v-if="showTextFilter">
        <el-input
          v-model="computedSearchText"
          clearable
          placeholder="Search"
          prefix-icon="el-icon-search"
          size="mini"
        ></el-input>
      </el-col>
      <el-col :span="4">
        <div style="text-align: right">
          <el-tooltip :content="`Add new ${itemName}`" v-if="canAdd">
            <i class="fa fa-plus-circle filter-btn filter-add" @click="handleCreate"></i>
          </el-tooltip>
          <el-tooltip content="Clear filters">
            <i class="fa fa-times-circle filter-btn filter-clear" @click="handleResetFilters"></i>
          </el-tooltip>
        </div>
      </el-col>
    </el-row>
    <el-row></el-row>
    <draggable
      class="draggable-group"
      draggable=".draggable"
      v-model="computedValue"
      v-infinite-scroll="handleLoadMore"
      :body-style="{ padding: '0' }"
      :class="{ 'draggable-fixed': isFixed }"
      :group="{ name: draggableGroup, pull: 'clone', put: false }"
      :id="draggableId"
      :sort="false"
    >
      <div
        class="item"
        v-for="item in computedValue"
        :key="item.id"
        :class="{
          draggable: !isDisabled(item.id) && isEditable,
          enabled: !isDisabled(item.id),
          disabled: isDisabled(item.id),
        }"
      >
        <el-card
          style="margin: 4px 0"
          shadow="never"
          :class="{ selected: item.id === selectedId, 'is-global': item.isGlobal || false }"
          :key="item.id"
          :body-style="itemStyle(item.id)"
          @click.native="handleSelect(item)"
          @dblclick.native="handleAddItem(item)"
        >
          <div class="handle pull-right">
            <i class="fa fa-ellipsis-v"></i>
            <i class="fa fa-ellipsis-v"></i>
          </div>
          <div class="options pull-right" v-if="item.isEditable || false">
            <a @click="handleEditItem(item)">
              <i class="fa fa-pencil"></i>
            </a>
          </div>
          <div>{{ item.title }}</div>
          <div class="clearfix"></div>
        </el-card>
      </div>
      <empty v-if="computedValue.length === 0">No {{ itemNamePlural }} found.</empty>
    </draggable>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import infiniteScroll from 'vue-infinite-scroll';
import Empty from '@/components/common/Empty';

export default {
  name: 'QuestionSelector',
  components: {
    draggable,
    Empty,
  },
  directives: { infiniteScroll },
  props: {
    itemName: {
      type: String,
      required: true,
    },
    itemNamePlural: {
      type: String,
      required: true,
    },
    draggableGroup: {
      type: String,
      default: 'defaultGroup',
    },
    canAdd: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      default: () => [],
    },
    categoryList: {
      type: Array,
      default: () => [],
    },
    disabledIds: {
      type: Array,
      default: () => [],
    },
    hasMore: {
      type: Boolean,
      default: true,
    },
    isFixed: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
    pool: {
      type: String,
      default: 'ALL',
    },
    showCategoryFilter: {
      type: Boolean,
      default: true,
    },
    showTextFilter: {
      type: Boolean,
      default: true,
    },
    selectedId: null,
    selectedCategoryId: null,
    searchText: '',
  },
  computed: {
    computedValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    computedCategory: {
      get() {
        return this.selectedCategoryId;
      },
      set(categoryId) {
        this.$emit('onChangeCategory', categoryId);
      },
    },
    computedSearchText: {
      get() {
        return this.searchText;
      },
      set(text) {
        this.$emit('onChangeSearchText', text);
      },
    },
    computedPool: {
      get() {
        return this.pool;
      },
      set(pool) {
        this.$emit('onChangePool', pool);
      },
    },
    draggableId() {
      return `draggable-${this.itemName.replace(' ', '-')}`;
    },
  },
  methods: {
    isDisabled(itemId) {
      return this.disabledIds.includes(itemId);
    },

    itemStyle(itemId) {
      const style = { padding: '8px' };
      if (this.isDisabled(itemId)) {
        return { ...style, color: '#ccc', backgroundColor: '#eee', fontStyle: 'oblique' };
      }

      return style;
    },

    handleAddItem(item) {
      if (this.isDisabled(item.id) || !this.isEditable) {
        return;
      }

      this.$emit('onItemAdd', item);
    },

    handleEditItem(item) {
      this.$emit('onItemEdit', item);
    },

    handleSelect(item) {
      this.$emit('onSelect', item);
    },

    handleCreate() {
      this.$emit('onCreate');
    },

    handleResetFilters() {
      this.$emit('onResetFilters');
    },

    handleLoadMore() {
      this.$emit('onLoadMore');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../assets/style/_variables_live.scss';
.draggable-group {
  overflow: auto;
  min-height: 225px;
  height: calc(100vh - 460px);
}

.draggable-fixed {
  height: calc(100vh - 250px);
}

.item {
  user-select: none;
}

.item.draggable {
  cursor: grab;
}

.item.disabled {
  cursor: default;
}

.item .selected {
  background-color: $colony-primary;
  color: $white;
}

.item .is-global {
  background-color: #d7ffd1;
}

.item .is-global.selected {
  background-color: $colony-green;
}

.handle {
  text-align: right;
  width: 2em;
  cursor: grab;
  color: #ccc;
}

.options {
  cursor: pointer;
  color: #ccc;
}

.options a {
  color: #ccc;
}

.filter-btn {
  font-size: 20px;
  margin: 4px 2px;
  cursor: pointer;
}

.filter-add {
  color: $colony-green;
}

.filter-clear {
  color: $colony-red;
}
</style>



// WEBPACK FOOTER //
// Selector.vue?73fa27fb